import React, {
  HTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from "react";
import { twMerge } from "tailwind-merge";

interface DataTableProps extends HTMLAttributes<HTMLTableElement> {
  wrapperClassName?: string;
}

const DataTableRoot: React.FC<DataTableProps> = ({
  className,
  wrapperClassName,
  children,
  ...rest
}) => (
  <div
    className={twMerge(
      "w-full overflow-x-auto rounded-lg border border-gray-200 bg-white",
      wrapperClassName
    )}
  >
    <table
      className={twMerge("w-full text-left text-sm text-gray-700", className)}
      {...rest}
    >
      {children}
    </table>
  </div>
);

const DataTableHead: React.FC<HTMLAttributes<HTMLTableSectionElement>> = ({
  className,
  children,
  ...rest
}) => (
  <thead
    className={twMerge(
      "border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500",
      className
    )}
    {...rest}
  >
    <tr>{children}</tr>
  </thead>
);

interface DataTableCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
}

const DataTableHeadCell: React.FC<DataTableCellProps> = ({
  className,
  numeric,
  children,
  ...rest
}) => (
  <th
    scope="col"
    className={twMerge(
      "px-6 py-3 font-medium",
      numeric && "text-right",
      className
    )}
    {...rest}
  >
    {children}
  </th>
);

const DataTableBody: React.FC<HTMLAttributes<HTMLTableSectionElement>> = ({
  className,
  children,
  ...rest
}) => (
  <tbody className={twMerge("divide-y divide-gray-100", className)} {...rest}>
    {children}
  </tbody>
);

const DataTableRow: React.FC<HTMLAttributes<HTMLTableRowElement>> = ({
  className,
  children,
  ...rest
}) => (
  <tr className={twMerge("hover:bg-gray-50", className)} {...rest}>
    {children}
  </tr>
);

interface DataTableBodyCellProps
  extends TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
}

const DataTableCell: React.FC<DataTableBodyCellProps> = ({
  className,
  numeric,
  children,
  ...rest
}) => (
  <td
    className={twMerge(
      "px-6 py-4 align-middle",
      numeric && "text-right tabular-nums",
      className
    )}
    {...rest}
  >
    {children}
  </td>
);

export const DataTable = Object.assign(DataTableRoot, {
  Head: DataTableHead,
  HeadCell: DataTableHeadCell,
  Body: DataTableBody,
  Row: DataTableRow,
  Cell: DataTableCell,
});

export default DataTable;
